import { useState } from 'react';
import { Loader2, Lock } from 'lucide-react';
import { useAuth } from '../../auth/useAuth';
import { startCheckout } from '../../lib/apiClient';
import { routes } from '../../lib/assessment/routes';
import { REPORT_PRICE } from '../../lib/pricing';
import { Button } from '../ui/button';

/**
 * Unlock CTA on the free result. Creates a server-priced Checkout Session and sends
 * the browser to the Stripe-hosted payment page. If the result is already unlocked,
 * it routes straight to the report instead.
 *
 * `navigate` is injectable so tests can assert the redirect target without touching
 * window.location.
 *
 * The price is read from `REPORT_PRICE` and is deliberately **not** a prop. It was
 * one, defaulting to a hardcoded `'$19.99'` that no call site ever overrode, which
 * meant this control — the one screen where a wrong price is most costly — was the
 * single place in the application that would not follow a price change. A prop with
 * a correct default would still leave that override open, so the prop is gone
 * rather than fixed, and drift is structurally impossible instead of merely
 * unlikely. `test/pricing.test.ts` holds the line for every other file.
 *
 * This is display copy. The amount charged is resolved server-side from Stripe and
 * never from this constant.
 */
export function CheckoutButton({
  resultId,
  navigate = (url: string) => window.location.assign(url),
}: {
  resultId: string;
  navigate?: (url: string) => void;
}) {
  const { status, session } = useAuth();
  // This control had no status gate at all, so a password-recovery session — one
  // that arrived from an emailed link and carries a valid token — could start a
  // real payment. The button is disabled whenever `token` is null.
  const token = status === 'signed-in' ? (session?.access_token ?? null) : null;
  const [state, setState] = useState<'idle' | 'starting' | 'error'>('idle');

  async function onClick() {
    if (!token || state === 'starting') return;
    setState('starting');
    const res = await startCheckout(token, resultId);
    if (res.state === 'ok') {
      navigate(res.url);
      return;
    }
    if (res.state === 'already-unlocked') {
      navigate(routes.report(resultId));
      return;
    }
    setState('error');
  }

  return (
    <div className="flex flex-col gap-2">
      {/*
        Production labels this control `Unlock Report $19.99` and marks it with
        a padlock, everywhere it appears — its unlock button takes an optional
        label override and no call site uses one. V2 said "Unlock the full
        report — $19.99", which is the same offer in different words on the one
        control that asks for money. Live comparison, 2026-09-07.
      */}
      <Button
        type="button"
        onClick={onClick}
        disabled={!token || state === 'starting'}
        data-testid="checkout-button"
      >
        {state === 'starting' ? (
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        ) : (
          <Lock className="h-4 w-4" aria-hidden="true" />
        )}
        {state === 'starting' ? 'Starting checkout…' : `Unlock Report $${REPORT_PRICE}`}
      </Button>
      {state === 'error' ? (
        <p className="text-sm text-destructive">Checkout could not be started. Please try again.</p>
      ) : null}
    </div>
  );
}
